import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, RefreshCw, Loader2, CheckCircle2, XCircle, TrendingUp, TrendingDown, Minus, AlertCircle, Lightbulb, Target, MessageSquareDashed, MessageSquare, CheckCircle, Hash, Layers } from 'lucide-react';
import { api } from '../../services/api';

interface GoalAssessment {
  goal: string;
  met: boolean;
  evidence: string;
  confidence: number;
}

interface SessionAnalyticsData {
  sessionId: string;
  projectId: string;
  status: 'generating' | 'ready';
  generatedAt: string;
  sentimentScore: number;
  sentimentLabel: string;
  sentimentSummary: string;
  goalsMetCount: number;
  goalsMissedCount: number;
  goalsAssessment: GoalAssessment[];
  improvements: string[];
  totalContributions: number;
  totalAccepted: number;
  totalRejections: number;
  phaseCount: number;
  topicCount: number;
  topicsDiscussed: string;
  frustrationScore: number;
}

interface Props {
  sessionId: string;
  projectId: string | null | undefined;
}

function SentimentBadge({ score, label }: { score: number; label: string }) {
  // Support both old (positive/neutral/negative) and new (high/medium/low) labels
  const isHigh = score >= 0.65 || label === 'high' || label === 'positive';
  const isLow = score < 0.4 || label === 'low' || label === 'negative';
  const Icon = isHigh ? TrendingUp : isLow ? TrendingDown : Minus;
  const displayLabel = label === 'high' ? 'High Engagement' : label === 'medium' ? 'Good Engagement' : label === 'low' ? 'Low Engagement' : label;
  const classes = isHigh
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : isLow
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${classes}`}>
      <Icon size={14} />
      {displayLabel}
      <span className="text-xs font-normal opacity-70">{Math.round(score * 100)}%</span>
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon }: { label: string; value: string | number; sub?: string; icon: React.ElementType }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
      <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
        <Icon size={48} className="text-primary" />
      </div>
      <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold text-navy">{value}</p>
        {sub && <span className="text-sm text-slate-400">{sub}</span>}
      </div>
    </div>
  );
}

export function SessionAnalyticsPanel({ sessionId, projectId }: Props) {
  const [data, setData] = useState<SessionAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkedImprovements, setCheckedImprovements] = useState<Set<number>>(new Set());
  const [showAllImprovements, setShowAllImprovements] = useState(false);
  const pollingRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get<SessionAnalyticsData>(`/api/sessions/${sessionId}/analytics`);
      if (res.data) {
        setData(res.data);
        if (res.data.status === 'ready') stopPolling();
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setData(null);
      }
      stopPolling();
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchAnalytics();
    return stopPolling;
  }, [fetchAnalytics]);

  useEffect(() => {
    if (data?.status === 'generating' && !pollingRef.current) {
      pollingRef.current = setInterval(fetchAnalytics, 5000);
    }
  }, [data?.status, fetchAnalytics]);

  const handleGenerate = async () => {
    setTriggering(true);
    setError(null);
    try {
      await api.post(`/api/sessions/${sessionId}/analytics/generate`, { projectId });
      setData(prev => prev ? { ...prev, status: 'generating' } : {
        sessionId, projectId: projectId || '', status: 'generating',
        generatedAt: new Date().toISOString(),
        sentimentScore: 0, sentimentLabel: 'neutral', sentimentSummary: '',
        goalsMetCount: 0, goalsMissedCount: 0, goalsAssessment: [], improvements: [],
        totalContributions: 0, totalAccepted: 0, totalRejections: 0,
        phaseCount: 0, topicCount: 0, topicsDiscussed: '', frustrationScore: 0,
      });
      if (!pollingRef.current) pollingRef.current = setInterval(fetchAnalytics, 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to trigger analytics');
    } finally {
      setTriggering(false);
    }
  };

  const isGenerating = data?.status === 'generating' || triggering;

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="w-full flex flex-col gap-6">

        {/* Header */}
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <BarChart3 size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Session Analytics</h1>
              {data?.generatedAt && (
                <p className="text-slate-500 text-sm font-medium">
                  Last run {new Date(data.generatedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-bold rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {isGenerating
              ? <><Loader2 size={15} className="animate-spin" /> Analysing...</>
              : <><RefreshCw size={15} /> Run Analytics</>
            }
          </button>
        </header>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 size={24} className="animate-spin mr-2" />
            <span className="text-sm">Loading analytics...</span>
          </div>
        )}

        {!loading && !data && !isGenerating && (
          <div className="text-center py-16 text-slate-400">
            <MessageSquareDashed size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No analytics yet</p>
            <p className="text-xs mt-1">Click Run Analytics to generate insights from session contributions.</p>
          </div>
        )}

        {isGenerating && !data?.status && (
          <div className="text-center py-16 text-slate-400">
            <Loader2 size={40} className="mx-auto mb-3 animate-spin opacity-60" />
            <p className="text-sm font-medium">Analysing session...</p>
            <p className="text-xs mt-1">This takes about 30 seconds.</p>
          </div>
        )}

        {data?.status === 'generating' && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
            <Loader2 size={16} className="animate-spin shrink-0" />
            Analysis in progress — results will appear automatically.
          </div>
        )}

        {data?.status === 'ready' && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Contributions" value={data.totalContributions} icon={MessageSquare} />
              <StatCard label="Accepted" value={data.totalAccepted} sub={`${data.totalContributions > 0 ? Math.round((data.totalAccepted / data.totalContributions) * 100) : 0}%`} icon={CheckCircle} />
              <StatCard label="Topics" value={data.topicCount} icon={Hash} />
              <StatCard label="Phases" value={data.phaseCount} icon={Layers} />
            </div>

            {/* Sentiment */}
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-700 text-sm">Session Sentiment</h3>
                <SentimentBadge score={data.sentimentScore} label={data.sentimentLabel} />
              </div>
              {data.sentimentSummary && (
                <p className="text-sm text-slate-600 leading-relaxed">{data.sentimentSummary}</p>
              )}
              {data.frustrationScore > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Frustration signals:</span>
                  <span className={`text-xs font-semibold ${data.frustrationScore > 10 ? 'text-red-600' : data.frustrationScore > 5 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {data.frustrationScore.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Goals */}
            {data.goalsAssessment.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Target size={16} className="text-primary" />
                  <h3 className="font-semibold text-slate-700 text-sm">Goal Attainment</h3>
                  <span className="ml-auto text-xs text-slate-500">
                    {data.goalsMetCount}/{data.goalsMetCount + data.goalsMissedCount} met
                  </span>
                </div>
                <div className="space-y-3">
                  {data.goalsAssessment.map((g) => (
                    <div key={g.goal} className={`flex gap-3 p-3 rounded-lg ${g.met ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                      <div className="shrink-0 mt-0.5">
                        {g.met
                          ? <CheckCircle2 size={16} className="text-emerald-600" />
                          : <XCircle size={16} className="text-red-500" />
                        }
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 leading-snug">{g.goal}</p>
                        {g.evidence && (
                          <p className="text-xs text-slate-500 mt-1 leading-snug">{g.evidence}</p>
                        )}
                        {g.confidence > 0 && (
                          <span className="text-[10px] text-slate-400 mt-1 inline-block">
                            {Math.round(g.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improvements */}
            {data.improvements.length > 0 && (() => {
              const VISIBLE = 3;
              const unchecked = data.improvements.map((s, i) => ({ s, i })).filter(({ i }) => !checkedImprovements.has(i));
              const checked = data.improvements.map((s, i) => ({ s, i })).filter(({ i }) => checkedImprovements.has(i));
              const visibleUnchecked = showAllImprovements ? unchecked : unchecked.slice(0, VISIBLE);
              const hiddenCount = unchecked.length - VISIBLE;
              const doneCount = checked.length;

              return (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb size={16} className="text-amber-500" />
                    <h3 className="font-semibold text-slate-700 text-sm">Suggested Improvements</h3>
                    {doneCount > 0 && (
                      <span className="ml-auto text-xs text-slate-400">{doneCount}/{data.improvements.length} done</span>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {visibleUnchecked.map(({ s, i }) => (
                      <li
                        key={s}
                        onClick={() => setCheckedImprovements(prev => { const next = new Set(prev); next.add(i); return next; })}
                        className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-amber-200 hover:bg-amber-50/40 cursor-pointer group transition-colors"
                      >
                        <div className="mt-0.5 w-4 h-4 rounded border-2 border-slate-300 group-hover:border-amber-400 shrink-0 transition-colors" />
                        <span className="text-sm text-slate-700 leading-snug">{s}</span>
                      </li>
                    ))}
                  </ul>

                  {!showAllImprovements && hiddenCount > 0 && (
                    <button
                      onClick={() => setShowAllImprovements(true)}
                      className="mt-3 text-xs text-slate-400 hover:text-primary transition-colors"
                    >
                      Show {hiddenCount} more
                    </button>
                  )}

                  {checked.length > 0 && (
                    <ul className={`space-y-2 ${unchecked.length > 0 ? 'mt-3 pt-3 border-t border-slate-100' : ''}`}>
                      {checked.map(({ s, i }) => (
                        <li
                          key={s}
                          onClick={() => setCheckedImprovements(prev => { const next = new Set(prev); next.delete(i); return next; })}
                          className="flex items-start gap-3 p-3 rounded-lg cursor-pointer group opacity-50 hover:opacity-70 transition-opacity"
                        >
                          <div className="mt-0.5 w-4 h-4 rounded border-2 border-emerald-400 bg-emerald-400 shrink-0 flex items-center justify-center">
                            <CheckCircle2 size={10} className="text-white" />
                          </div>
                          <span className="text-sm text-slate-500 leading-snug line-through">{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

            {/* Topics */}
            {data.topicsDiscussed && (
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-semibold text-slate-700 text-sm mb-3">Topics Discussed</h3>
                <div className="flex flex-wrap gap-2">
                  {data.topicsDiscussed.split(',').map(t => t.trim()).filter(Boolean).map((topic) => (
                    <span key={topic} className="px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}


export default SessionAnalyticsPanel;
